// frontend/src/hooks/usePhotoSubject.ts
/**
 * Everything the compare page needs for a subject's photos
 * (docs/plans/2026-10-29-livestock-compare-design.md, 8.1): its index, its
 * reference, saving a line up, making a photo the reference, the share target
 * and the words. One photo's read is `useSubjectPhoto`, one call per side, as
 * hooks can't be called a varying number of times.
 *
 * Both branches' hooks are always called, the other branch's disabled through
 * an undefined id (docs/plans/2026-10-29-livestock-compare-plan.md, 4.3 and
 * 5.1). An undefined subject fetches nothing, as `useTankPhoto` does for an
 * undefined tank. The share target is the subject's kind: a tank_compare
 * link for tank shots, an organism_compare one for an organism (plan 7.5).
 */

import { useCallback, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTank } from '@/contexts/TankContext';
import {
  useSetTankCover,
  useSetTankPhotoFraming,
  useTankPhoto,
  useTankPhotoIndex,
} from '@/hooks/useTankPhotos';
import {
  useOrganism,
  useOrganismPhoto,
  useOrganismPhotoIndex,
  useSetOrganismPhotoFraming,
  useSetOrganismReference,
} from '@/hooks/useLivestockPhotos';
import type { ComparePair } from '@/features/livestock/compareShots';
import {
  organismReferenceOf,
  referenceOf,
  type LineUpReference,
} from '@/features/livestock/lineUpReference';
import {
  ORGANISM_WORDS,
  TANK_WORDS,
  type PhotoSubject,
  type SubjectWords,
} from '@/features/livestock/photoSubject';
import type { ComparePhoto, PhotoFraming, PhotoIndex } from '@/types/photos';
import type { ShareLinkCreate } from '@/types/shareLinks';

export interface PhotoSubjectApi {
  index: UseQueryResult<PhotoIndex, Error>;
  reference: LineUpReference | null;
  words: SubjectWords;
  /** Save a framing, or null to reset. Resolves on success; notifies and rejects on failure. */
  saveFraming: (id: number, framing: PhotoFraming | null) => Promise<void>;
  /** Make this photo the reference. Resolves on success; notifies and rejects on failure. */
  makeReference: (id: number) => Promise<void>;
  /** The share target for a pair, or null while the subject can't be shared. */
  shareTarget: ((pair: ComparePair) => ShareLinkCreate) | null;
}

function tankShareTarget(pair: ComparePair): ShareLinkCreate {
  return { kind: 'tank_compare', target: { before_id: pair.beforeId, after_id: pair.afterId } };
}

export function usePhotoSubject(subject: PhotoSubject | undefined): PhotoSubjectApi {
  const tankId = subject?.kind === 'tank' ? subject.tankId : undefined;
  const organismTankId = subject?.kind === 'organism' ? subject.tankId : undefined;
  const livestockId = subject?.kind === 'organism' ? subject.livestockId : undefined;
  const isOrganism = organismTankId !== undefined && livestockId !== undefined;
  const { tanks, selectedTank } = useTank();

  // Tank shots
  const tankIndex = useTankPhotoIndex(tankId);
  // Disabled without a tank: the callbacks below refuse rather than call them.
  const { mutateAsync: setFraming } = useSetTankPhotoFraming(tankId ?? '');
  const { mutateAsync: setCover } = useSetTankCover(tankId ?? '');

  // An organism's photos
  const organismIndex = useOrganismPhotoIndex(organismTankId, livestockId);
  const { mutateAsync: setOrganismFraming } = useSetOrganismPhotoFraming(
    organismTankId ?? '',
    livestockId ?? 0
  );
  const { mutateAsync: setOrganismReference } = useSetOrganismReference(
    organismTankId ?? '',
    livestockId ?? 0
  );
  const organism = useOrganism(organismTankId, livestockId);

  // The selected tank first: its cover fields are the freshest (and the compare
  // page has always read them from it); another tank's from the list.
  const tank =
    tankId === undefined
      ? undefined
      : selectedTank?.id === tankId
        ? selectedTank
        : tanks.find((candidate) => candidate.id === tankId);
  const tankReference = useMemo(() => referenceOf(tank), [tank]);
  const organismReference = useMemo(() => organismReferenceOf(organism.data), [organism.data]);

  const saveFraming = useCallback(
    async (id: number, framing: PhotoFraming | null): Promise<void> => {
      if (tankId !== undefined) await setFraming({ id, framing });
      else if (isOrganism) await setOrganismFraming({ id, framing });
      else throw new Error('No photo subject to save to');
    },
    [setFraming, setOrganismFraming, tankId, isOrganism]
  );
  const makeReference = useCallback(
    async (id: number): Promise<void> => {
      if (tankId !== undefined) await setCover(id);
      else if (isOrganism) await setOrganismReference(id);
      else throw new Error('No photo subject to save to');
    },
    [setCover, setOrganismReference, tankId, isOrganism]
  );

  // One function per organism, so the target keeps its identity across renders
  const organismShareTarget = useMemo(
    () =>
      livestockId === undefined
        ? null
        : (pair: ComparePair): ShareLinkCreate => ({
            kind: 'organism_compare',
            target: { livestock_id: livestockId, before_id: pair.beforeId, after_id: pair.afterId },
          }),
    [livestockId]
  );

  return {
    index: isOrganism ? organismIndex : tankIndex,
    reference: isOrganism ? organismReference : tankReference,
    words: isOrganism ? ORGANISM_WORDS : TANK_WORDS,
    saveFraming,
    makeReference,
    shareTarget: tankId !== undefined ? tankShareTarget : isOrganism ? organismShareTarget : null,
  };
}

/** One photo of the subject with its signed variants; disabled without a subject or an id. */
export function useSubjectPhoto(
  subject: PhotoSubject | undefined,
  id: number | undefined
): UseQueryResult<ComparePhoto, Error> {
  const tankShot = useTankPhoto(subject?.kind === 'tank' ? subject.tankId : undefined, id);
  const organismPhoto = useOrganismPhoto(
    subject?.kind === 'organism' ? subject.tankId : undefined,
    subject?.kind === 'organism' ? subject.livestockId : undefined,
    id
  );
  return subject?.kind === 'organism' ? organismPhoto : tankShot;
}
